import {
    REPORT_SETTINGS_DEFAULT_REPORT_KEY,
    ReportSettingsConcreteReportKey,
    ReportSettingsReportKey,
} from "@common/types/reports/report-settings-report-key";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { AccountingReportType } from "@common/types/reports/report-type";
import {
    EMPTY_REPORT_SETTING_VALUES,
    ReportSettingsOwner,
    ReportSettingsStoredRow,
    StoredReportSettingValues,
    readReportSettingValues,
} from "./report-setting-fields";
import { ReportSettingSource } from "./report-setting-source";

export interface AppliedReportTemplate {
    id: string | null;
}

export type WorkspaceScopedReportSettingsTarget =
    | { kind: "entity"; entityId: number }
    | { kind: "workspace" };

export interface TemplateReportSettingsTarget {
    kind: "template";
    templateId: string;
}

export type ReportSettingsTarget =
    | WorkspaceScopedReportSettingsTarget
    | TemplateReportSettingsTarget;

export interface WorkspaceReportSettingsScope {
    workspaceId: string;
    entityTemplate?: AppliedReportTemplate;
    workspaceTemplate?: AppliedReportTemplate;
    firmDefaultTemplate?: { id: string };
}

interface ReportSettingsResolutionBase {
    reportKey: ReportSettingsConcreteReportKey;
    rows: ReportSettingsStoredRow[];
}

export interface WorkspaceScopedResolutionInput
    extends ReportSettingsResolutionBase, WorkspaceReportSettingsScope {
    target: WorkspaceScopedReportSettingsTarget;
}

export interface TemplateResolutionInput extends ReportSettingsResolutionBase {
    target: TemplateReportSettingsTarget;
}

export type ReportSettingsResolutionInput =
    | WorkspaceScopedResolutionInput
    | TemplateResolutionInput;

export interface ReportSettingCandidate {
    source: ReportSettingSource;
    values: StoredReportSettingValues;
}

interface Place {
    kind: ReportSettingSource["kind"];
    owner: ReportSettingsOwner;
}

export const isSameOwner = (
    left: ReportSettingsOwner,
    right: ReportSettingsOwner,
): boolean => {
    if (left.kind === "template" && right.kind === "template") {
        return left.templateId === right.templateId;
    }
    if (left.kind === "workspace" && right.kind === "workspace") {
        return left.workspaceId === right.workspaceId;
    }
    if (left.kind === "entity" && right.kind === "entity") {
        return left.entityId === right.entityId;
    }
    return false;
};

interface ToOwnerInput {
    target: ReportSettingsTarget;
    workspaceId?: string;
}

export const toReportSettingsOwner = ({
    target,
    workspaceId,
}: ToOwnerInput): ReportSettingsOwner => {
    switch (target.kind) {
        case "entity":
            return { kind: "entity", entityId: target.entityId };
        case "template":
            return { kind: "template", templateId: target.templateId };
        case "workspace":
            if (workspaceId === undefined) {
                throw new Error("A workspace owner needs a workspace id");
            }
            return { kind: "workspace", workspaceId };
        default: {
            const exhaustiveCheck: never = target;
            return exhaustiveCheck;
        }
    }
};

interface TemplatePlaceInput {
    kind: Place["kind"];
    templateId: string | null | undefined;
}

const templatePlace = ({ kind, templateId }: TemplatePlaceInput): Place[] =>
    templateId ? [{ kind, owner: { kind: "template", templateId } }] : [];

const workspacePlaces = ({
    workspaceId,
    workspaceTemplate,
    firmDefaultTemplate,
}: WorkspaceScopedResolutionInput): Place[] => [
    {
        kind: "workspace-override",
        owner: { kind: "workspace", workspaceId },
    },
    ...(workspaceTemplate
        ? templatePlace({
              kind: "workspace-template",
              templateId: workspaceTemplate.id,
          })
        : templatePlace({
              kind: "firm-default-template",
              templateId: firmDefaultTemplate?.id,
          })),
];

const isTemplateResolutionInput = (
    input: ReportSettingsResolutionInput,
): input is TemplateResolutionInput => input.target.kind === "template";

const getPlaces = (input: ReportSettingsResolutionInput): Place[] => {
    if (isTemplateResolutionInput(input)) {
        const { templateId } = input.target;
        return [{ kind: "template", owner: { kind: "template", templateId } }];
    }
    const { target, entityTemplate } = input;
    if (target.kind === "workspace") {
        return workspacePlaces(input);
    }
    const entityOverride: Place = {
        kind: "entity-override",
        owner: { kind: "entity", entityId: target.entityId },
    };
    if (entityTemplate) {
        return [
            entityOverride,
            ...templatePlace({
                kind: "entity-template",
                templateId: entityTemplate.id,
            }),
        ];
    }
    return [entityOverride, ...workspacePlaces(input)];
};

const toGeneralLedgerRowVisibility = (
    rowVisibility: ReportSettingRowVisibility | null,
): ReportSettingRowVisibility | null =>
    rowVisibility === null ||
    rowVisibility === ReportSettingRowVisibility.ALL_ACCOUNTS
        ? rowVisibility
        : ReportSettingRowVisibility.GL_STANDARD;

interface CandidateValuesInput {
    row: ReportSettingsStoredRow | undefined;
    reportKey: ReportSettingsReportKey;
    targetReportKey: ReportSettingsConcreteReportKey;
}

const readCandidateValues = ({
    row,
    reportKey,
    targetReportKey,
}: CandidateValuesInput): StoredReportSettingValues => {
    const values = row
        ? readReportSettingValues(row)
        : EMPTY_REPORT_SETTING_VALUES;
    return reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY &&
        targetReportKey === AccountingReportType.GENERAL_LEDGER
        ? {
              ...values,
              rowVisibility: toGeneralLedgerRowVisibility(values.rowVisibility),
          }
        : values;
};

const reportKeysToCheck = (
    reportKey: ReportSettingsConcreteReportKey,
): ReportSettingsReportKey[] => [reportKey, REPORT_SETTINGS_DEFAULT_REPORT_KEY];

export const buildReportSettingCandidates = (
    input: ReportSettingsResolutionInput,
): ReportSettingCandidate[] =>
    getPlaces(input).flatMap(({ kind, owner }) =>
        reportKeysToCheck(input.reportKey).map((reportKey) => {
            const row = input.rows.find(
                (candidate) =>
                    candidate.reportKey === reportKey &&
                    isSameOwner(candidate.owner, owner),
            );
            return {
                source: {
                    kind,
                    reportKey,
                    ...(owner.kind === "template"
                        ? { templateId: owner.templateId }
                        : {}),
                },
                values: readCandidateValues({
                    row,
                    reportKey,
                    targetReportKey: input.reportKey,
                }),
            };
        }),
    );
